import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent,
  type ChangeEvent,
} from 'react'
import { Send, FileText, Paperclip } from 'lucide-react'
import { useSendMessage } from '../../hooks/useSendMessage.js'
import { useAuthStore } from '../../store/authStore.js'
import { useVisualViewport } from '../../hooks/useVisualViewport.js'
import { useUploadAttachment } from '../../hooks/useUploadAttachment.js'
import { getSocket } from '../../socket/client.js'
import SessionWindowBadge from './SessionWindowBadge.js'
import TemplatePickerDialog from './TemplatePickerDialog.js'
import TemplateVariableForm from './TemplateVariableForm.js'
import EmojiPicker from './EmojiPicker.js'
import { renderTemplate } from '../../utils/templateVars.js'
import type { ChannelOrigin, ConversationStatus, Message, MessageAttachment, WhatsAppTemplate } from './types.js'
import { cn } from '../../utils/cn.js'
import { timestampMs } from '../../utils/timezone.js'

interface PendingAttachment {
  localId: string
  file: File
  name: string
  mimeType: string
  sizeBytes: number
  state: 'uploading' | 'uploaded' | 'error'
  attachmentId?: string
  error?: string
}

export interface MessageComposerProps {
  conversationId: string
  channelOrigin: ChannelOrigin
  status: ConversationStatus
  /** From conversation detail (UCP-0c). null = no inbound yet. */
  sessionExpiresAt: string | null
  /** §0.G step 6: text to repopulate the textarea (edit-and-resubmit). */
  initialDraft?: string
  /** Bumped each time initialDraft should be (re)adopted. */
  draftSignal?: number
  onSend: (tempId: string, optimistic: Message) => void
  onSendSucceeded: (tempId: string, realMessage: Message) => void
  onSendFailed: (tempId: string, error: string) => void
}

function smsSegments(n: number): number {
  if (n === 0) return 0
  if (n <= 160) return 1
  return Math.ceil(n / 153)
}

export default function MessageComposer(props: MessageComposerProps): JSX.Element {
  const [text, setText] = useState('')
  const [attachmentIds, setAttachmentIds] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const [_resetSignal, setResetSignal] = useState(0)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pendingTemplate, setPendingTemplate] = useState<WhatsAppTemplate | null>(null)
  const [sentForApproval, setSentForApproval] = useState(false)
  const [cardMasked, setCardMasked] = useState(false)
  const { send, isPending } = useSendMessage()
  const agentId = useAuthStore((s) => s.user?.sub) ?? null
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const cursorRef = useRef<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Inline attachment state
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([])
  const { upload } = useUploadAttachment()

  const vv = useVisualViewport()
  const keyboardGap =
    typeof window === 'undefined'
      ? 0
      : Math.max(0, window.innerHeight - vv.height - vv.offsetTop)

  const isClosed = props.status === 'closed'
  const isWhatsApp = props.channelOrigin === 'whatsapp'
  const outside24h =
    isWhatsApp &&
    (props.sessionExpiresAt === null ||
      timestampMs(props.sessionExpiresAt) < Date.now())

  const disabled = isClosed || outside24h
  const hasContent = text.trim().length > 0 || attachmentIds.length > 0
  const canSend = !disabled && hasContent && !isPending && !uploading

  // Auto-grow textarea
  const adjustHeight = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.overflow = 'hidden'
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`
  }, [])

  // §0.E a11y: focus moves to the composer when a new conversation is selected.
  useEffect(() => {
    if (!disabled) {
      textareaRef.current?.focus()
      adjustHeight()
    }
  }, [props.conversationId, disabled, adjustHeight])

  // §0.G step 6: adopt rejected message text
  const draftSignal = props.draftSignal ?? 0
  useEffect(() => {
    if (draftSignal > 0) {
      setText(props.initialDraft ?? '')
      textareaRef.current?.focus()
      requestAnimationFrame(adjustHeight)
    }
  }, [draftSignal])

  // Listen for "focus-composer" event from keyboard nav
  useEffect(() => {
    const handler = () => {
      textareaRef.current?.focus()
    }
    window.addEventListener('conversation:focus-composer', handler)
    return () => window.removeEventListener('conversation:focus-composer', handler)
  }, [])

  // Clear typing indicator when the composer unmounts or conversation changes.
  useEffect(() => {
    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
      if (props.channelOrigin === 'chat' && props.conversationId) {
        getSocket().emit('chat:typing:stop', { sessionId: props.conversationId })
      }
    }
  }, [props.channelOrigin, props.conversationId])

  async function submit(): Promise<void> {
    if (!canSend) return
    const body = text
    const ids = attachmentIds

    // Live chat is socket-only; bypass the REST /conversations/:id/messages endpoint.
    if (props.channelOrigin === 'chat') {
      const chatAttachments = pendingAttachments
        .filter((p) => p.state === 'uploaded' && p.attachmentId)
        .map((p) => ({ attachmentId: p.attachmentId!, filename: p.name, mimeType: p.mimeType, sizeBytes: p.sizeBytes }))
      const chatAttachmentRecords: MessageAttachment[] = pendingAttachments
        .filter((p) => p.state === 'uploaded' && p.attachmentId)
        .map((p) => ({ id: p.attachmentId!, mimeType: p.mimeType, filename: p.name, sizeBytes: p.sizeBytes }))
      // Match the ws-gateway default bodyText for attachment-only messages
      // (server falls back to '[media]' when content.trim() is empty but
      // attachments are present). Without this, LiveChatThread's pending-own
      // lookup by body fails and the socket event appends a duplicate.
      const chatBody = body.trim() || (chatAttachmentRecords.length ? '[media]' : '')
      const socket = getSocket()
      const tempId = `optimistic-${crypto.randomUUID()}`
      const optimistic: Message = {
        id: tempId,
        conversationId: props.conversationId,
        direction: 'outbound',
        senderHandle: null,
        senderAgentId: agentId,
        senderAgentName: null,
        senderName: null,
        body: chatBody,
        attachments: chatAttachmentRecords,
        status: 'sending',
        createdAt: new Date().toISOString(),
      }
      props.onSend(tempId, optimistic)
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
      socket.emit('chat:typing:stop', { sessionId: props.conversationId })
      setText('')
      setAttachmentIds([])
      setPendingAttachments([])
      setResetSignal((n) => n + 1)
      requestAnimationFrame(() => {
        const el = textareaRef.current
        if (el) { el.style.height = 'auto' }
      })
      socket.emit(
        'chat:message:send',
        {
          sessionId: props.conversationId,
          content: body,
          ...(chatAttachments.length ? { attachments: chatAttachments } : {}),
        },
        (result) => {
          if (result?.ok && result.messageId) {
            props.onSendSucceeded(tempId, { ...optimistic, id: result.messageId, status: 'sent' })
          } else {
            props.onSendFailed(tempId, result?.error ?? 'SEND_FAILED')
          }
        },
      )
      return
    }

    const tempId = `optimistic-${crypto.randomUUID()}`
    const attachmentRecords: MessageAttachment[] = pendingAttachments
      .filter((p) => p.state === 'uploaded' && p.attachmentId)
      .map((p) => ({ id: p.attachmentId!, mimeType: p.mimeType, filename: p.name, sizeBytes: p.sizeBytes }))
    const optimistic: Message = {
      id: tempId,
      conversationId: props.conversationId,
      direction: 'outbound',
      senderHandle: null,
      senderAgentId: agentId,
      senderAgentName: null,
      senderName: null,
      body,
      attachments: attachmentRecords,
      status: 'sending',
      createdAt: new Date().toISOString(),
    }
    props.onSend(tempId, optimistic)
    setText('')
    setAttachmentIds([])
    setPendingAttachments([])
    setResetSignal((n) => n + 1)
    setSentForApproval(false)
    requestAnimationFrame(() => {
      const el = textareaRef.current
      if (el) { el.style.height = 'auto' }
    })
    try {
      const resp = await send(props.conversationId, body, ids)
      if (resp.status === 'pending_approval') {
        props.onSendSucceeded(tempId, {
          ...optimistic,
          id: resp.messageId,
          status: 'pending_approval',
        })
        setSentForApproval(true)
        return
      }
      setCardMasked(resp.cardDataMasked === true)
      props.onSendSucceeded(tempId, {
        ...optimistic,
        body: resp.cardDataMasked && resp.maskedText ? resp.maskedText : optimistic.body,
        id: resp.messageId,
        status: 'sent',
      })
    } catch (err) {
      props.onSendFailed(tempId, (err as Error).message)
    }
  }

  async function submitTemplate(
    template: WhatsAppTemplate,
    variables: string[],
  ): Promise<void> {
    const tempId = `optimistic-${crypto.randomUUID()}`
    const optimistic: Message = {
      id: tempId,
      conversationId: props.conversationId,
      direction: 'outbound',
      senderHandle: null,
      senderAgentId: agentId,
      senderAgentName: null,
      senderName: null,
      body: renderTemplate(template.bodyText, variables),
      attachments: [],
      status: 'sending',
      createdAt: new Date().toISOString(),
    }
    props.onSend(tempId, optimistic)
    setPendingTemplate(null)
    setPickerOpen(false)
    try {
      const resp = await send(props.conversationId, optimistic.body, undefined, {
        name: template.metaTemplateName,
        languageCode: template.languageCode,
        variables,
      })
      props.onSendSucceeded(tempId, {
        ...optimistic,
        id: resp.messageId,
        status: 'sent',
      })
    } catch (err) {
      props.onSendFailed(tempId, (err as Error).message)
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Enter' && !e.shiftKey && (props.channelOrigin === 'chat' || e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      void submit()
    }
  }

  // ---- Inline attachment handling ----
  const handleFileChange = useCallback(() => {
    const files = fileInputRef.current?.files
    if (!files || files.length === 0) return
    const newFiles = Array.from(files)
    console.log('[MessageComposer] handleFileChange', { count: newFiles.length, files: newFiles.map(f => ({ name: f.name, size: f.size, type: f.type })) })
    const additions: PendingAttachment[] = newFiles.map((file) => ({
      localId: crypto.randomUUID(),
      file,
      name: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
      state: 'uploading',
    }))
    setPendingAttachments((prev) => [...prev, ...additions])
    for (const a of additions) {
      void (async () => {
        try {
          console.log('[MessageComposer] Starting upload', { localId: a.localId, name: a.name })
          const res = await upload(a.file)
          console.log('[MessageComposer] Upload succeeded', { localId: a.localId, attachmentId: res.attachmentId })
          setPendingAttachments((prev) =>
            prev.map((p) =>
              p.localId === a.localId
                ? { ...p, state: 'uploaded', attachmentId: res.attachmentId, mimeType: res.mimeType, sizeBytes: res.sizeBytes }
                : p,
            ),
          )
          setAttachmentIds((prev) => [...prev, res.attachmentId])
          setUploading(false)
        } catch (err) {
          console.error('[MessageComposer] Upload FAILED', { localId: a.localId, name: a.name, error: (err as Error).message })
          setPendingAttachments((prev) =>
            prev.map((p) =>
              p.localId === a.localId ? { ...p, state: 'error', error: (err as Error).message } : p,
            ),
          )
          setUploading(false)
        }
      })()
    }
    setUploading(true)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [upload])

  const removeAttachment = useCallback((localId: string) => {
    setPendingAttachments((prev) => {
      const removed = prev.find((p) => p.localId === localId)
      const next = prev.filter((p) => p.localId !== localId)
      if (removed?.attachmentId) {
        setAttachmentIds((ids) => ids.filter((id) => id !== removed.attachmentId))
      }
      return next
    })
  }, [])

  function insertEmoji(emoji: string): void {
    const el = textareaRef.current
    const start = cursorRef.current ?? el?.selectionStart ?? text.length
    const before = text.slice(0, start)
    const after = text.slice(start)
    const newText = before + emoji + after
    setText(newText)
    const newCursor = before.length + emoji.length
    requestAnimationFrame(() => {
      if (el) {
        el.focus()
        el.setSelectionRange(newCursor, newCursor)
        cursorRef.current = newCursor
      }
    })
  }

  return (
    <footer
      className="shrink-0 border-t border-gray-100 bg-white px-4 py-3"
      style={keyboardGap > 0 ? { paddingBottom: keyboardGap + 12 } : undefined}
    >
      {/* Status banners */}
      {isWhatsApp && (
        <div className="mb-2">
          <SessionWindowBadge
            channelOrigin={props.channelOrigin}
            sessionExpiresAt={props.sessionExpiresAt}
          />
        </div>
      )}
      {isClosed && (
        <div className="mb-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-500">
          This conversation is closed. Re-open to reply.
        </div>
      )}
      {outside24h && !isClosed && !pendingTemplate && (
        <div className="mb-2">
          <button
            onClick={() => setPickerOpen(true)}
            type="button"
            className="flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition-all hover:bg-blue-100 active:scale-[0.97]"
          >
            <FileText className="h-3.5 w-3.5" />
            Use template
          </button>
        </div>
      )}

      {/* Attachment pills */}
      {pendingAttachments.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {pendingAttachments.map((p) => (
            <span
              key={p.localId}
              className={cn(
                'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px]',
                p.state === 'error'
                  ? 'border-red-200 bg-red-50 text-red-700'
                  : p.state === 'uploading'
                    ? 'border-gray-200 bg-gray-50 text-gray-500'
                    : 'border-gray-200 bg-gray-50 text-gray-700',
              )}
            >
              <span className="max-w-[120px] truncate">{p.name}</span>
              {p.state === 'uploading' && (
                <span className="h-2.5 w-2.5 animate-spin rounded-full border border-gray-300 border-t-gray-600" />
              )}
              {p.state === 'error' && <span className="text-red-400">!</span>}
              <button
                type="button"
                onClick={() => removeAttachment(p.localId)}
                className="ml-0.5 text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {pendingTemplate ? (
        <TemplateVariableForm
          template={pendingTemplate}
          onSubmit={(variables) => submitTemplate(pendingTemplate, variables)}
          onCancel={() => setPendingTemplate(null)}
        />
      ) : (
        /* WhatsApp-style single rounded bar */
        <div className="flex items-end gap-1 rounded-2xl border border-gray-200 bg-gray-50 p-1.5 transition-all focus-within:border-gray-300 focus-within:bg-white focus-within:shadow-sm">
          {/* Attachment */}
          {!disabled && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileChange}
                aria-label="Attach files"
                data-testid="attachment-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                title="Attach files"
                className="mb-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-700 active:scale-95"
              >
                <Paperclip className="h-[18px] w-[18px]" />
              </button>
              <EmojiPicker onSelect={insertEmoji} />
            </>
          )}

          {/* Auto-growing textarea - no scrollbar */}
          <textarea
            ref={textareaRef}
            aria-label="Type a reply"
            className={cn(
              'min-h-[36px] flex-1 resize-none bg-transparent px-1 py-2 text-sm leading-5 text-gray-800 outline-none placeholder:text-gray-400',
              disabled && 'cursor-not-allowed',
            )}
            style={{ overflow: 'hidden' }}
            value={text}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
              setText(e.target.value)
              cursorRef.current = e.currentTarget.selectionStart
              if (sentForApproval) setSentForApproval(false)
              if (cardMasked) setCardMasked(false)
              requestAnimationFrame(adjustHeight)
              if (props.channelOrigin === 'chat' && !disabled) {
                const socket = getSocket()
                socket.emit('chat:typing:start', { sessionId: props.conversationId })
                if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
                typingTimerRef.current = setTimeout(() => {
                  socket.emit('chat:typing:stop', { sessionId: props.conversationId })
                }, 1500)
              }
            }}
            onKeyDown={onKeyDown}
            onKeyUp={(e) => { cursorRef.current = e.currentTarget.selectionStart }}
            onMouseUp={(e) => { cursorRef.current = e.currentTarget.selectionStart }}
            onFocus={(e) => { cursorRef.current = e.currentTarget.selectionStart }}
            disabled={disabled}
            placeholder={disabled ? '' : 'Type a reply…'}
            rows={1}
          />

          {/* Send */}
          <button
            onClick={() => void submit()}
            disabled={!canSend}
            aria-disabled={!canSend}
            title="Send (Cmd/Ctrl+Enter)"
            type="button"
            className={cn(
              'mb-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all active:scale-95',
              canSend
                ? 'bg-[#16273F] text-white shadow-sm hover:bg-[#1e3657]'
                : 'text-gray-400',
            )}
          >
            {isPending ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
      )}

      {isWhatsApp && (
        <TemplatePickerDialog
          visible={pickerOpen}
          onHide={() => setPickerOpen(false)}
          onSelect={(t) => {
            setPickerOpen(false)
            setPendingTemplate(t)
          }}
        />
      )}
      {cardMasked && (
        <div
          role="status"
          aria-live="polite"
          data-testid="card-masked-notice"
          className="mt-2 rounded-lg border border-amber-100 bg-amber-50 px-3 py-1.5 text-xs text-amber-700"
        >
          A card number was found in your message and was masked before sending. Do not ask customers to type card numbers in chat.
        </div>
      )}
      {sentForApproval && (
        <div
          role="status"
          aria-live="polite"
          data-testid="sent-for-approval"
          className="mt-2 rounded-lg border border-amber-100 bg-amber-50 px-3 py-1.5 text-xs text-amber-700"
        >
          Sent for approval — awaiting a supervisor decision.
        </div>
      )}
      {props.channelOrigin === 'sms' && text.length > 0 && (
        <div className="mt-1 text-right text-[10px] text-gray-400">
          {text.length <= 160
            ? `${text.length}/160`
            : `${text.length} chars, ${smsSegments(text.length)} segments`}
        </div>
      )}
    </footer>
  )
}
